import React, { useState, useEffect } from 'react';
import { Input, Spinner, Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { fuelAPI, vehiclesAPI, projectsAPI } from '../../helpers/api';

interface Vehicle {
  id: string;
  plate: string;
  name?: string;
  brand?: string;
  model?: string;
  type?: string;
}

interface Project {
  id: string;
  name: string;
  code?: string;
}

interface Site {
  id: string;
  name: string;
  code?: string;
  projectId: string;
}

interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
}

interface FuelRecord {
  id: string;
  date: string;
  quantity: number;
  totalCost: number;
  unitPrice?: number;
  fuelType?: string;
  odometer?: number;
  station?: string;
  invoiceNumber?: string;
  notes?: string;
  vehicleId: string;
  projectId?: string;
  siteId?: string;
  filledById?: string;
  vehicle: {
    id: string;
    plate: string;
    name?: string;
    brand?: string;
    model?: string;
  };
  project?: {
    id: string;
    name: string;
    code?: string;
  };
  site?: {
    id: string;
    name: string;
    code?: string;
  };
  filledBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface FuelDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  fuelRecord: FuelRecord | null;
  onUpdate?: () => void;
}

const FUEL_TYPES = [
  { value: 'DIESEL', label: 'Dizel' },
  { value: 'GASOLINE', label: 'Benzin' },
  { value: 'LPG', label: 'LPG' },
];

const getFuelTypeLabel = (type?: string) => {
  const found = FUEL_TYPES.find((ft) => ft.value === type);
  return found?.label || type || '-';
};

const getFuelTypeStyle = (type?: string) => {
  switch (type) {
    case 'DIESEL':
      return 'bg-dark bg-opacity-10 text-dark';
    case 'GASOLINE':
      return 'bg-success bg-opacity-10 text-success';
    case 'LPG':
      return 'bg-info bg-opacity-10 text-info';
    default:
      return 'bg-secondary bg-opacity-10 text-secondary';
  }
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatCurrency = (value: number) => {
  return value.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const FuelDetailModal: React.FC<FuelDetailModalProps> = ({
  isOpen,
  toggle,
  fuelRecord,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    vehicleId: '',
    quantity: '',
    totalCost: '',
    fuelType: '',
    odometer: '',
    station: '',
    customStation: '',
    filledById: '',
    invoiceNumber: '',
    projectId: '',
    siteId: '',
    notes: '',
  });

  // Data lists
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [sitePersonnel, setSitePersonnel] = useState<Personnel[]>([]);
  const [stations, setStations] = useState<string[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);

  // Reset form when fuelRecord changes
  useEffect(() => {
    if (fuelRecord && isOpen) {
      setFormData({
        date: fuelRecord.date.split('T')[0],
        vehicleId: fuelRecord.vehicleId,
        quantity: fuelRecord.quantity.toString(),
        totalCost: fuelRecord.totalCost.toString(),
        fuelType: fuelRecord.fuelType || '',
        odometer: fuelRecord.odometer?.toString() || '',
        station: fuelRecord.station || '',
        customStation: '',
        filledById: fuelRecord.filledById || '',
        invoiceNumber: fuelRecord.invoiceNumber || '',
        projectId: fuelRecord.projectId || '',
        siteId: fuelRecord.siteId || '',
        notes: fuelRecord.notes || '',
      });
      setIsEditing(false);

      // Fetch sites and personnel if set
      if (fuelRecord.projectId) {
        fetchSites(fuelRecord.projectId);
      }
      if (fuelRecord.siteId) {
        fetchSitePersonnel(fuelRecord.siteId);
      }
    }
  }, [fuelRecord, isOpen]);

  // Update station field when stations are loaded
  useEffect(() => {
    if (fuelRecord && stations.length > 0) {
      const stationInList = stations.includes(fuelRecord.station || '');
      setFormData((prev) => ({
        ...prev,
        station: stationInList ? (fuelRecord.station || '') : (fuelRecord.station ? '__OTHER__' : ''),
        customStation: stationInList ? '' : (fuelRecord.station || ''),
      }));
    }
  }, [fuelRecord, stations]);

  const fetchEditData = async () => {
    try {
      const [vehiclesRes, projectsRes, stationsRes] = await Promise.all([
        vehiclesAPI.getAll({ isActive: true }),
        projectsAPI.getAll(),
        fuelAPI.getStations(),
      ]);
      setVehicles(vehiclesRes.data);
      setProjects(projectsRes.data);
      setStations(stationsRes.data);
    } catch (error) {
      console.error('Edit data fetch error:', error);
    }
  };

  const fetchSites = async (projectId: string) => {
    if (projectId) {
      try {
        const response = await projectsAPI.getProjectSites(projectId);
        setSites(response.data);
      } catch (error) {
        console.error('Sites fetch error:', error);
        setSites([]);
      }
    } else {
      setSites([]);
    }
  };

  const fetchSitePersonnel = async (siteId: string) => {
    if (siteId) {
      try {
        setLoadingPersonnel(true);
        const response = await projectsAPI.getSitePersonnel(siteId);
        // API returns sitePersonnel with nested personnel object
        const personnelList = response.data.map((sp: any) => sp.personnel);
        setSitePersonnel(personnelList);
      } catch (error) {
        console.error('Site personnel fetch error:', error);
        setSitePersonnel([]);
      } finally {
        setLoadingPersonnel(false);
      }
    } else {
      setSitePersonnel([]);
    }
  };

  const handleProjectChange = async (projectId: string) => {
    setFormData({ ...formData, projectId, siteId: '', filledById: '' });
    setSitePersonnel([]);
    await fetchSites(projectId);
  };

  const handleSiteChange = async (siteId: string) => {
    setFormData({ ...formData, siteId, filledById: '' });
    await fetchSitePersonnel(siteId);
  };

  const getActualStation = () =>
    formData.station === '__OTHER__' ? formData.customStation : formData.station;

  const calculatedUnitPrice =
    formData.quantity && formData.totalCost
      ? (parseFloat(formData.totalCost) / parseFloat(formData.quantity)).toFixed(2)
      : '';

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!fuelRecord) return;

    if (!formData.vehicleId || !formData.quantity || !formData.totalCost) {
      toast.error('Araç, litre ve tutar zorunludur');
      return;
    }

    try {
      setSaving(true);
      const actualStation = getActualStation();

      await fuelAPI.update(fuelRecord.id, {
        date: formData.date,
        vehicleId: formData.vehicleId,
        quantity: parseFloat(formData.quantity),
        totalCost: parseFloat(formData.totalCost),
        fuelType: formData.fuelType || undefined,
        odometer: formData.odometer ? parseInt(formData.odometer) : undefined,
        station: actualStation || undefined,
        filledById: formData.filledById || undefined,
        invoiceNumber: formData.invoiceNumber || undefined,
        projectId: formData.projectId || undefined,
        siteId: formData.siteId || undefined,
        notes: formData.notes || undefined,
      });
      toast.success('Yakıt kaydı güncellendi');
      setIsEditing(false);
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (fuelRecord) {
      setFormData({
        date: fuelRecord.date.split('T')[0],
        vehicleId: fuelRecord.vehicleId,
        quantity: fuelRecord.quantity.toString(),
        totalCost: fuelRecord.totalCost.toString(),
        fuelType: fuelRecord.fuelType || '',
        odometer: fuelRecord.odometer?.toString() || '',
        station: fuelRecord.station || '',
        customStation: '',
        filledById: fuelRecord.filledById || '',
        invoiceNumber: fuelRecord.invoiceNumber || '',
        projectId: fuelRecord.projectId || '',
        siteId: fuelRecord.siteId || '',
        notes: fuelRecord.notes || '',
      });
      // Re-fetch sites and personnel for edit mode
      if (fuelRecord.projectId) {
        fetchSites(fuelRecord.projectId);
      }
      if (fuelRecord.siteId) {
        fetchSitePersonnel(fuelRecord.siteId);
      }
    }
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!fuelRecord) return;

    if (!window.confirm('Bu yakıt kaydını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await fuelAPI.delete(fuelRecord.id);
      toast.success('Yakıt kaydı silindi');
      toggle();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  if (!fuelRecord) return null;

  const headerActions = (
    <>
      {!isEditing ? (
        <>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={() => {
              fetchEditData();
              if (fuelRecord.projectId) fetchSites(fuelRecord.projectId);
              if (fuelRecord.siteId) fetchSitePersonnel(fuelRecord.siteId);
              setIsEditing(true);
            }}
            title="Düzenle"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleDelete}
            title="Sil"
            style={{ color: '#f06548' }}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleSave}
            disabled={saving}
            title="Kaydet"
            style={{ color: saving ? '#adb5bd' : '#0ab39c' }}
          >
            {saving ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
          </button>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleCancel}
            disabled={saving}
            title="İptal"
          >
            <i className="ri-close-line"></i>
          </button>
        </>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Yakıt Kaydı Detayı"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-start justify-content-between">
            <div style={{ flex: 1, marginRight: '1rem' }}>
              {isEditing ? (
                <Input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="form-control-lg mb-2"
                  style={{ fontWeight: 600, maxWidth: '200px' }}
                />
              ) : (
                <h4 className="mb-1" style={{ fontWeight: 600 }}>
                  {formatDate(fuelRecord.date)}
                </h4>
              )}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="px-2 py-1 rounded-pill small bg-primary bg-opacity-10 text-primary" style={{ fontWeight: 500 }}>
                  {fuelRecord.vehicle.plate}
                </span>
                {fuelRecord.fuelType && (
                  <span className={`px-2 py-1 rounded-pill small ${getFuelTypeStyle(fuelRecord.fuelType)}`} style={{ fontWeight: 500 }}>
                    {getFuelTypeLabel(fuelRecord.fuelType)}
                  </span>
                )}
              </div>
            </div>
            <div className="text-end">
              <div className="text-success" style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                {formatCurrency(fuelRecord.totalCost)} ₺
              </div>
              <small className="text-muted">
                {fuelRecord.quantity.toLocaleString('tr-TR')} lt
              </small>
            </div>
          </div>
        </div>

        {/* Yakıt Bilgileri Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
          <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            <i className="ri-gas-station-line me-1"></i>
            Yakıt Bilgileri
          </div>
          <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
            {/* Araç */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Araç</span>
              {isEditing ? (
                <Input
                  type="select"
                  name="vehicleId"
                  value={formData.vehicleId}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '200px' }}
                >
                  <option value="">Seçin</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.plate} {v.name ? `(${v.name})` : ''}
                    </option>
                  ))}
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {fuelRecord.vehicle.plate}
                  {fuelRecord.vehicle.name && ` (${fuelRecord.vehicle.name})`}
                  {fuelRecord.vehicle.brand && fuelRecord.vehicle.model && ` - ${fuelRecord.vehicle.brand} ${fuelRecord.vehicle.model}`}
                </span>
              )}
            </div>

            {/* Yakıt Türü */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Yakıt Türü</span>
              {isEditing ? (
                <Input
                  type="select"
                  name="fuelType"
                  value={formData.fuelType}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '120px' }}
                >
                  <option value="">Seçin</option>
                  {FUEL_TYPES.map((ft) => (
                    <option key={ft.value} value={ft.value}>
                      {ft.label}
                    </option>
                  ))}
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>{getFuelTypeLabel(fuelRecord.fuelType)}</span>
              )}
            </div>

            {/* Miktar */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Miktar</span>
              {isEditing ? (
                <Input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '120px' }}
                  step="0.01"
                />
              ) : (
                <span style={{ fontWeight: 500 }}>{fuelRecord.quantity.toLocaleString('tr-TR')} lt</span>
              )}
            </div>

            {/* Tutar */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Tutar</span>
              {isEditing ? (
                <Input
                  type="number"
                  name="totalCost"
                  value={formData.totalCost}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '120px' }}
                  step="0.01"
                />
              ) : (
                <span style={{ fontWeight: 500 }}>{formatCurrency(fuelRecord.totalCost)} ₺</span>
              )}
            </div>

            {/* Birim Fiyat */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Birim Fiyat</span>
              <span style={{ fontWeight: 500 }}>
                {isEditing && calculatedUnitPrice
                  ? `${calculatedUnitPrice} ₺/lt`
                  : fuelRecord.unitPrice
                  ? `${Number(fuelRecord.unitPrice).toFixed(2)} ₺/lt`
                  : '-'}
              </span>
            </div>

            {/* Kilometre */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2">
              <span className="text-muted">Kilometre</span>
              {isEditing ? (
                <Input
                  type="number"
                  name="odometer"
                  value={formData.odometer}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '120px' }}
                  placeholder="Km"
                />
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {fuelRecord.odometer ? `${fuelRecord.odometer.toLocaleString('tr-TR')} km` : '-'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Proje / Şantiye Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
          <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            <i className="ri-building-line me-1"></i>
            Proje / Şantiye
          </div>
          <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
            {/* Proje */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Proje</span>
              {isEditing ? (
                <Input
                  type="select"
                  value={formData.projectId}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  bsSize="sm"
                  style={{ width: '200px' }}
                >
                  <option value="">Seçin</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {fuelRecord.project?.name || '-'}
                </span>
              )}
            </div>

            {/* Şantiye */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Şantiye</span>
              {isEditing ? (
                <Input
                  type="select"
                  value={formData.siteId}
                  onChange={(e) => handleSiteChange(e.target.value)}
                  bsSize="sm"
                  style={{ width: '200px' }}
                  disabled={!formData.projectId || sites.length === 0}
                >
                  <option value="">Seçin</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {fuelRecord.site?.name || '-'}
                </span>
              )}
            </div>

            {/* Yakıtı Alan */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2">
              <span className="text-muted">Yakıtı Alan</span>
              {isEditing ? (
                loadingPersonnel ? (
                  <Spinner size="sm" color="primary" />
                ) : formData.siteId && sitePersonnel.length > 0 ? (
                  <Input
                    type="select"
                    value={formData.filledById}
                    onChange={(e) => setFormData({ ...formData, filledById: e.target.value })}
                    bsSize="sm"
                    style={{ width: '200px' }}
                  >
                    <option value="">Seçin</option>
                    {sitePersonnel.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.firstName} {p.lastName}
                      </option>
                    ))}
                  </Input>
                ) : (
                  <span className="text-muted small">
                    {!formData.siteId ? 'Önce şantiye seçin' : 'Personel yok'}
                  </span>
                )
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {fuelRecord.filledBy
                    ? `${fuelRecord.filledBy.firstName} ${fuelRecord.filledBy.lastName}`
                    : '-'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* İstasyon Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
          <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            <i className="ri-store-2-line me-1"></i>
            İstasyon / Tedarikçi
          </div>
          <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
            {/* İstasyon */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">İstasyon</span>
              {isEditing ? (
                <div className="d-flex flex-column align-items-end gap-1">
                  <Input
                    type="select"
                    value={formData.station}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        station: value,
                        customStation: value === '__OTHER__' ? formData.customStation : '',
                      });
                    }}
                    bsSize="sm"
                    style={{ width: '200px' }}
                  >
                    <option value="">Seçin</option>
                    {stations.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                    <option value="__OTHER__">Diğer</option>
                  </Input>
                  {formData.station === '__OTHER__' && (
                    <Input
                      type="text"
                      value={formData.customStation}
                      onChange={(e) => setFormData({ ...formData, customStation: e.target.value })}
                      bsSize="sm"
                      style={{ width: '200px' }}
                      placeholder="Yeni istasyon"
                    />
                  )}
                </div>
              ) : (
                <span style={{ fontWeight: 500 }}>{fuelRecord.station || '-'}</span>
              )}
            </div>

            {/* Fiş No */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2">
              <span className="text-muted">Fiş/Fatura No</span>
              {isEditing ? (
                <Input
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '200px' }}
                  placeholder="Belge no"
                />
              ) : (
                <span style={{ fontWeight: 500 }}>{fuelRecord.invoiceNumber || '-'}</span>
              )}
            </div>
          </div>
        </div>

        {/* Notlar Card */}
        {(fuelRecord.notes || isEditing) && (
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
            <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-file-text-line me-1"></i>
              Notlar
            </div>
            {isEditing ? (
              <Input
                type="textarea"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Notlar..."
              />
            ) : (
              <p className="mb-0">{fuelRecord.notes || '-'}</p>
            )}
          </div>
        )}

        {/* Kayıt Bilgisi Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3">
          <div className="text-muted small">
            <i className="ri-user-line me-1"></i>
            Kaydeden: {fuelRecord.createdBy.firstName} {fuelRecord.createdBy.lastName}
          </div>
        </div>
      </div>
    </SideModal>
  );
};

export default FuelDetailModal;
